import { EmployeeColumnId } from "@/types/column.types.ts";
import { isEmployeeColumnKey } from "@/types/employees/employee-column.types.ts";

/**
 * Ключ каталога → legacy-`EmployeeColumnId`. Соответствие фиксировано бэкендом
 * (`EMPLOYEE_COLUMN_KEYS` в `employee_column_constants.py`) и нужно в двух местах:
 * рендер системной ячейки берётся по legacy-id, а старые сохранённые вьюхи
 * хранят именно legacy-id и миграцию на ключи делают при пересохранении.
 *
 * Имени сотрудника в каталоге нет: оно приходит всегда и остаётся фиксированной
 * колонкой таблицы.
 */
export const LEGACY_ID_BY_COLUMN_KEY: Record<string, EmployeeColumnId> = {
  // Личные данные
  "employee.middle_name": EmployeeColumnId.middle_name,
  "employee.name_pronunciation": EmployeeColumnId.name_pronunciation,
  "employee.lifecycle_status": EmployeeColumnId.lifecycle_status,
  "employee.last_seen": EmployeeColumnId.last_seen,
  "employee.hired_on": EmployeeColumnId.hired_on,
  "employee.birth_date": EmployeeColumnId.birth_date,
  "employee.personnel_number": EmployeeColumnId.personnel_number,
  "employee.gender": EmployeeColumnId.gender,
  "employee.address": EmployeeColumnId.address,
  "employee.address_country": EmployeeColumnId.address_country,
  "employee.address_city": EmployeeColumnId.address_city,
  "employee.address_state": EmployeeColumnId.address_state,
  "employee.address_zip": EmployeeColumnId.address_zip,
  "employee.address_line_1": EmployeeColumnId.address_line_1,
  "employee.address_line_2": EmployeeColumnId.address_line_2,
  "employee.timezone": EmployeeColumnId.timezone,
  "employee.email": EmployeeColumnId.email,
  "employee.email_personal": EmployeeColumnId.email_personal,
  "employee.phone": EmployeeColumnId.phone,
  "employee.phone_personal": EmployeeColumnId.phone_personal,
  "employee.slack": EmployeeColumnId.slack,

  // Позиция
  "employee.position.job_profile": EmployeeColumnId.employee_position,
  "employee.position.job_role": EmployeeColumnId.job_role,
  "employee.position.effective_from": EmployeeColumnId.position_date_effective_from,
  "employee.position.department": EmployeeColumnId.department,
  "employee.position.division": EmployeeColumnId.division,
  "employee.position.manager": EmployeeColumnId.manager,
  "employee.position.location": EmployeeColumnId.location,
  "employee.position.legal_entity": EmployeeColumnId.legal_entity,
  "employee.position.department_manager": EmployeeColumnId.department_manager,
  "employee.position.change_reason": EmployeeColumnId.employee_position_change_reason,
  "employee.position.comment": EmployeeColumnId.position_comment,

  // Занятость
  "employee.employment.contract": EmployeeColumnId.contract,
  "employee.employment.type": EmployeeColumnId.employment_type,
  "employee.employment.effective_from": EmployeeColumnId.employment_date_effective_from,
  "employee.employment.effective_to": EmployeeColumnId.employment_date_effective_to,
  "employee.employment.probation_period_end": EmployeeColumnId.probation_period_end,
  "employee.employment.work_pattern": EmployeeColumnId.work_pattern,
  "employee.employment.comment": EmployeeColumnId.employment_comment,

  // Увольнение
  "employee.termination.date": EmployeeColumnId.termination_date,
  "employee.termination.reason": EmployeeColumnId.termination_reason,
  "employee.termination.last_working_date": EmployeeColumnId.last_working_date,
  "employee.termination.leave_type": EmployeeColumnId.termination_leave_type,
  "employee.termination.replacement_employee": EmployeeColumnId.replacement_employee,
  "employee.termination.eligible_for_rehire": EmployeeColumnId.eligible_for_rehire,

  // Компенсация
  "employee.compensation.base_salary_gross": EmployeeColumnId.base_salary_gross,
  "employee.compensation.base_salary_net": EmployeeColumnId.base_salary_net,
  "employee.compensation.total_cost": EmployeeColumnId.compensation_total_cost,
  "employee.compensation.per_type": EmployeeColumnId.compensation_per_type,
  "employee.compensation.change_reason": EmployeeColumnId.compensation_change_reason,
  "employee.compensation.effective_from": EmployeeColumnId.compensation_date_effective_from,
  "employee.compensation.currency": EmployeeColumnId.compensation_currency,
  "employee.compensation.comment": EmployeeColumnId.compensation_comment,
};

const COLUMN_KEY_BY_LEGACY_ID: Record<string, string> = Object.fromEntries(
  Object.entries(LEGACY_ID_BY_COLUMN_KEY).map(([key, legacyId]) => [legacyId, key])
);

/** Legacy-id системной колонки по ключу каталога. */
export function getLegacyColumnId(columnId: string): EmployeeColumnId | undefined {
  return LEGACY_ID_BY_COLUMN_KEY[columnId];
}

/**
 * Приводит id из сохранённой вьюхи к ключу каталога. Старые вьюхи хранят
 * значение `EmployeeColumnId` либо slug кастомного поля; бэк понимает их в
 * экспорте, но при пересохранении вьюхи писать нужно ключи.
 *
 * Значение, которого нет среди legacy-id, считается slug кастомного поля —
 * так же, как это делает `EmployeeFieldKey.from_legacy_column_id` на бэке.
 */
export function migrateLegacyColumnId(id: string): string {
  if (isEmployeeColumnKey(id)) return id;
  return COLUMN_KEY_BY_LEGACY_ID[id] ?? `employee.custom_field.${id}`;
}
